import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { insideImages } from '../data/insideImages';
import { Ornament } from './Ornament';
type InsideCarouselProps = {
  id?: string;
  title?: string;
  subtitle?: string;
  seeItems?: string[];
  footnote?: string;
};
const defaultSeeItems = ['See the positions.', 'See the movement sequences.', 'See the progressions.', 'See the corrections.'];
export function InsideCarousel({
  id = 'inside',
  title = 'See Inside Stretching Instructor Ready',
  subtitle = 'Real pages from the 100% visual core teaching system.',
  seeItems = defaultSeeItems,
  footnote = 'See exactly how each class is structured before you teach it.'
}: InsideCarouselProps) {
  const [[index, direction], setState] = useState<[number, number]>([0, 0]);
  const total = insideImages.length;
  const go = (step: number) => {
    setState(([current]) => [(current + step + total) % total, step]);
  };
  const image = insideImages[index];
  const navClasses = 'absolute top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-accent/50 bg-surface/95 text-primary-strong shadow-card transition-colors duration-150 hover:bg-surface hover:text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-subtle';
  return <section id={id} className="border-y border-primary/10 bg-surface-subtle px-4 py-20 sm:py-28" data-id="element-193" data-mp="35psc9ih1vsaz">
      <div className="mx-auto max-w-5xl" data-id="element-194" data-mp="3fhppsvvj6wcv">
        <div className="text-center" data-id="element-195" data-mp="3fir37700ruxr">
          <h2 className="mx-auto max-w-3xl text-3xl font-semibold uppercase leading-tight tracking-wide text-foreground sm:text-4xl" data-id="element-196" data-mp="3fjsoe4ar1xt7">
            {title}
          </h2>
          <Ornament className="mt-5" data-id="element-197" data-mp="3ojp5nkut7ue8" />
          <p className="mx-auto mt-4 max-w-2xl text-base text-muted-foreground" data-id="element-198" data-mp="3ogjiwc9nl4lo">
            {subtitle}
          </p>
          {seeItems.length > 0 && <ul className="mx-auto mt-5 flex max-w-2xl flex-wrap justify-center gap-x-4 gap-y-1 text-sm font-semibold text-primary-strong" data-id="element-199" data-mp="1tg10k9y2mzq2">
              {seeItems.map(item => <li key={item} data-id="element-200" data-mp="235au7u8kcqga">{item}</li>)}
            </ul>}
          {footnote && <p className="mt-2 text-sm text-muted-foreground" data-id="element-201" data-mp="1ydixt32q7qi8">{footnote}</p>}
        </div>

        <div className="relative mx-auto mt-12 max-w-xl" role="region" aria-roledescription="carousel" aria-label={title} data-id="element-202" data-mp="2d1vwi7t77ngf">
          <div className="relative overflow-hidden rounded-2xl border border-accent/30 bg-surface p-3 shadow-card" data-id="element-203" data-mp="2m4ex0l9ihnzi">
            <div className="relative aspect-[4/5] overflow-hidden rounded-xl bg-background" data-id="element-204" data-mp="2h5vcmpakgk99">
              <AnimatePresence initial={false} custom={direction} mode="popLayout" data-id="element-205" data-mp="rqx74rlmwf1b">
                <motion.img key={image.src} src={image.src} alt={image.alt} custom={direction} initial={{
                opacity: 0,
                x: direction >= 0 ? 40 : -40
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: direction >= 0 ? -40 : 40
              }} transition={{
                duration: 0.25,
                ease: [0.23, 1, 0.32, 1]
              }} drag="x" dragConstraints={{
                left: 0,
                right: 0
              }} dragElastic={0.2} onDragEnd={(_, info) => {
                if (info.offset.x < -60) go(1);
                if (info.offset.x > 60) go(-1);
              }} className="absolute inset-0 h-full w-full cursor-grab object-contain active:cursor-grabbing" draggable={false} data-id="element-206" data-mp="mrarkbic2mpc" />
              </AnimatePresence>
            </div>
          </div>

          <button type="button" onClick={() => go(-1)} aria-label="Previous image" className={`${navClasses} left-0 -translate-x-1/3 sm:-translate-x-[130%]`} data-id="element-207" data-mp="4wdcc9hb5aii">
            <ChevronLeftIcon size={18} aria-hidden="true" data-id="element-208" data-mp="3g9kn03ygfwyn" />
          </button>
          <button type="button" onClick={() => go(1)} aria-label="Next image" className={`${navClasses} right-0 translate-x-1/3 sm:translate-x-[130%]`} data-id="element-209" data-mp="3bdly8f97scxp">
            <ChevronRightIcon size={18} aria-hidden="true" data-id="element-210" data-mp="31h0ojgm92dnj" />
          </button>

          <div className="mt-6 flex justify-center gap-2" data-id="element-211" data-mp="2xedsf3ijq6kj">
            {insideImages.map((item, dotIndex) => <button key={item.src} type="button" onClick={() => setState([dotIndex, dotIndex > index ? 1 : -1])} aria-label={`Show image ${dotIndex + 1}`} aria-current={dotIndex === index} className={`h-2.5 rounded-full transition-[width,background-color] duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${dotIndex === index ? 'w-7 bg-primary' : 'w-2.5 bg-primary/25 hover:bg-primary/50'}`} data-id="element-212" data-mp="2sk9nsqwr6amm" data-mpi={dotIndex} />)}
          </div>
        </div>
      </div>
    </section>;
}